import React from 'react'
import styled from '@emotion/styled'
import Button from 'library/atoms/Button'
import useTranslation from 'next-translate/useTranslation'
import { useField } from 'shared/providers/FormProvider/formProvider'

export interface QuantityProps {
  /**
   * Needed for formik
   */
  name: string
  /**
   * Takes a number. corresponds to minimum quantity allowed
   */
  min?: number
  /**
   * Takes a number. corresponds to maximum quantity allowed
   */
  max: number | undefined
  incrementAriaLabel?: string
  decrementAriaLabel?: string
  incrementInactive?: boolean | null
  onChange?: (value: number) => void
  gaid?: string
}

const Quantity = ({
  min,
  max,
  name,
  incrementAriaLabel = 'Increase quantity',
  decrementAriaLabel = 'Decrease quantity',
  incrementInactive,
  onChange,
  gaid,
  ...formikProps
}: QuantityProps) => {
  const { field, helpers } = useField<number>(name)
  const { t } = useTranslation('common')

  const getNewQuantityValue = (value: number, direction: 'up' | 'down') => {
    if (direction === 'up') {
      if (typeof max === 'undefined') {
        return value + 1
      }
      return value + 1 <= max ? value + 1 : max
    }
    if (typeof min === 'undefined') {
      return value - 1
    }
    return value - 1 >= min ? value - 1 : min
  }

  const changeQuantity = (direction: 'up' | 'down') => {
    const newValue = getNewQuantityValue(field.value, direction)
    helpers.setValue(newValue)
    onChange && onChange(newValue)
  }

  return (
    <StyledQuantity>
      <StyledButton
        /**
         * In future if we want to handle blur use handleBlur for onClick instead of onBlur
         * because the buttons should only trigger blur for the input via click.
         * For this case onBlur doesn't work as good as onClick.
         * Check history of this file for implementation.
         * Note that focusing on the button causes a visual bug on iphone.
         */
        type="button"
        data-gaid={gaid}
        onClick={() => {
          changeQuantity('down')
        }}
        variant="secondary"
        inactive={min !== undefined && field.value <= min}
        aria-label={decrementAriaLabel ?? t('decrease-quantity')}
      >
        -
      </StyledButton>
      <StyledInput
        data-gaid={gaid}
        id={name}
        type="number"
        pattern="[0-9]"
        max={max}
        min={min}
        required
        {...field}
        {...formikProps}
        onBlur={e => field.onBlur(e)}
      />
      <StyledButton
        data-gaid={gaid}
        type="button"
        onClick={() => {
          changeQuantity('up')
        }}
        inactive={incrementInactive || (!!max && field.value >= max)}
        variant="secondary"
        aria-label={incrementAriaLabel ?? t('increase-quantity')}
      >
        +
      </StyledButton>
    </StyledQuantity>
  )
}

export default Quantity

const BUTTON_SIZE = 40

const StyledQuantity = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.space[1] - 1, // minus the 1px added on border
  border: theme.border,
  borderRadius: BUTTON_SIZE + theme.space[1] * 2,
  width: theme.space[1] + BUTTON_SIZE + 32 + BUTTON_SIZE + theme.space[1]
}))

const StyledInput = styled('input')(({ theme }) => ({
  maxWidth: 33, // set max-width because of issue with type="number"
  border: 0,
  outline: 0,
  textAlign: 'center',
  ...theme.fontStyle.h6,
  // hide default arrows
  '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '&[type=number]': {
    MozAppearance: 'textfield'
  }
}))

const StyledButton = styled(Button)(({ theme, inactive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: BUTTON_SIZE,
  height: BUTTON_SIZE,
  borderRadius: '50%',
  padding: 0,
  userSelect: 'none',
  color: theme.colors.primary,
  ...(inactive && {
    color: theme.colors.state.disabled,
    background: 'transparent',
    borderColor: 'transparent'
  })
}))
